import { z } from "zod";
import { calendarDateSchema } from "./dateWindow.js";
/**
 * Schema for POST /api/v1/accounts-receivable/orgs/:orgId/:invoiceId/payment.
 *
 * Wraps the `process_ar_payment` SECURITY DEFINER RPC, which moved behind
 * `verify_backend_token()` in Pattern C (see migration
 * 20260519003153_pattern_c_ap_ar_payment_rpcs.sql). The invoice id lives in
 * the URL path, the rest in the body — mirrors the clearing endpoints and
 * the AP-side schema.
 */
export const processARPaymentSchema = z
    .object({
    payment_amount: z.number().positive(),
    payment_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, {
        message: "payment_date must be YYYY-MM-DD",
    }),
    payment_method: z.string().min(1),
    bank_account_id: z.string().uuid(),
    ar_account_id: z.string().uuid(),
    reference: z.string().nullable().optional(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
})
    .strict();
/**
 * Schemas for the AR invoice CRUD routes (apps/api/src/routes/accounting/ar-invoices.ts).
 *
 * Wrap create_ar_invoice_with_line_items / update_ar_invoice_with_line_items /
 * delete_ar_invoice / update_ar_invoice_status. The create/update/delete RPCs
 * write to the ledger, so the frontend must route them through the backend
 * before the RESTRICTIVE verify_backend_token() ledger policy lands (SCRUM-184;
 * HIGH-1/HIGH-2). delete_ar_invoice is converted DEFINER→INVOKER in Phase II.
 */
/**
 * One AR line on the wire. EXPORTED because the Sales Channels sale schema
 * (`createChannelSaleSchema`, schemas/salesChannels.ts) posts the very same
 * line shape — create_channel_sale's line loop is a copy of
 * create_ar_invoice_with_line_items' — and a second declaration would be free
 * to drift from this one.
 */
export const arLineItemSchema = z
    .object({
    service_category_id: z.string().uuid().optional(),
    revenue_account_id: z.string().uuid(),
    description: z.string(),
    quantity: z.number(),
    unit_price: z.number(),
    tax_rate: z.number(),
    tax_rate_id: z.string().uuid().optional(),
    tax_treatment: z.string().optional(),
    tax_inclusive: z.boolean().optional(),
    sort_order: z.number().int(),
    exemption_reason: z.string().optional(),
    exemption_reason_other: z.string().optional(),
})
    .strict();
/**
 * One AR line on the AR invoice create/update wire: the base line plus the
 * optional rebill link `source_expense_id` (migration 20260928141345,
 * `invoice_line_items.source_expense_id`) — the expense this line bills on to
 * the customer. The RPCs coerce an id that is not an expense of the invoice's
 * org to NULL, so a stale link never fails a save.
 *
 * Used ONLY by `arInvoiceBodyShape`. Channel sales keep `arLineItemSchema`
 * (their RPC has no such column), and `recurringArInvoicePayloadSchema`
 * overrides `line_items` back to the base shape so a template can never
 * carry a link (every generated invoice would re-bill the same expense).
 */
export const arInvoiceLineItemSchema = arLineItemSchema
    .extend({
    source_expense_id: z.string().uuid().optional(),
})
    .strict();
// Shared body for create + update (invoice_id lives in the URL for update).
const arInvoiceBodyShape = {
    business_id: z.string().uuid().nullable().optional(),
    person_id: z.string().uuid().nullable().optional(),
    customer_name: z.string().min(1),
    customer_email: z.string().nullable().optional(),
    /**
     * The billing address printed on the invoice document, snapshotted onto
     * `accounts_receivable.client_address`.
     *
     * Deliberately NOT `.nullable()`, unlike its siblings: this field carries
     * THREE caller intentions and only two of them are a value. ABSENT means
     * "say nothing about the address", and the RPC keeps whatever is stored —
     * which is what makes a web bundle older than the migration harmless. `''`
     * means "the user cleared the field", and the RPC stores NULL, which the
     * document assembler reads as "follow the customer record". Allowing `null`
     * too would give two spellings of one of those and no way to tell which the
     * caller meant.
     */
    customer_address: z.string().optional(),
    /**
     * `accounts_receivable.invoice_date` is a plain `date` column. TRANSITION
     * (one release): the datetime-local shape is still accepted so a stale web
     * tab open across the deploy keeps working — `'2026-03-15T14:30'::date`
     * casts fine in SQL. Tighten to date-only (`/^\d{4}-\d{2}-\d{2}$/`) next
     * release.
     */
    invoice_date: z.string().regex(/^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2})?$/, {
        message: "invoice_date must be yyyy-MM-dd or yyyy-MM-dd'T'HH:mm",
    }),
    due_date: z.string().min(1),
    description: z.string(),
    reference: z.string().nullable().optional(),
    ar_account_id: z.string().uuid(),
    project_id: z.string().uuid().nullable().optional(),
    fund_id: z.string().uuid().nullable().optional(),
    /**
     * The supply date (the day the goods or services were delivered,
     * `metadata.supply_date`, 20261101120000) — a calendar day. Absent or null
     * means "the same as the invoice date"; the RPC stores it only when it
     * differs. Editable on a draft only: Issue keeps it (or the draft's own
     * date) and an issued invoice refuses a change with `ISSUED_LOCKED`. Not on
     * the recurring payload — see `recurringArInvoicePayloadSchema`.
     */
    supply_date: calendarDateSchema.nullable().optional(),
    line_items: z.array(arInvoiceLineItemSchema).min(1),
};
export const createARInvoiceSchema = z.object(arInvoiceBodyShape).strict();
export const updateARInvoiceSchema = z.object(arInvoiceBodyShape).strict();
/**
 * PATCH .../ar-invoices/:invoiceId/status — the overdue flag only
 * (`update_ar_invoice_status`, 20261101120000). Draft → sent is Issue,
 * → cancelled is Cancel, and paid / partially_paid are derived from the
 * payments, so `sent` ↔ `overdue` is all that is left.
 */
export const AR_INVOICE_SETTABLE_STATUSES = ["sent", "overdue"];
export const updateARInvoiceStatusSchema = z
    .object({ status: z.enum(AR_INVOICE_SETTABLE_STATUSES) })
    .strict();
/**
 * POST .../ar-invoices/:invoiceId/issue — `issue_ar_invoice`. Numbers and
 * books a draft. `issueDate` absent = the org's today. The date must be on or
 * before today, on or after the last issued INV invoice (cancelled included)
 * and outside a filed VAT period; a refusal answers 400 with its code (see
 * `AR_INVOICE_ERROR_CODES`).
 */
export const issueARInvoiceSchema = z
    .object({ issueDate: calendarDateSchema.optional() })
    .strict();
/**
 * POST .../ar-invoices/:invoiceId/cancel — `cancel_ar_invoice`. Reverses an
 * issued invoice with nothing received, on `cancelDate` (absent = the org's
 * today; on or before today, on or after the invoice date, not in a filed VAT
 * period). The `reason` is REQUIRED: the reversal journal is the evidence of
 * the correction (EU VAT Directive art. 90; Wet OB art. 29; CGI art. 272), so
 * a blank one is a 400 here and `REASON_REQUIRED` from the RPC. The
 * credit-note document comes with Peppol; the dialog tells the user to send
 * one.
 */
export const cancelARInvoiceSchema = z
    .object({
    reason: z.string().trim().min(1).max(500),
    cancelDate: calendarDateSchema.optional(),
})
    .strict();
/**
 * POST .../ar-invoices/:invoiceId/fund — `update_ar_invoice_fund`. Moves an
 * ISSUED, UNPAID invoice (and its posting: the journal header and every
 * entry) to another fund, or clears it (`fundId: null`). Fund is not a VAT
 * dimension, so this is allowed on a declared invoice and in a filed VAT
 * period too (the `update_expense_header` precedent): amounts, dates and VAT
 * never move. Refused `USE_EDIT` on a draft (its fund is edited in the
 * form), `CANCELLED`, `HAS_PAYMENTS` once money is received (a receivable is
 * never split across two funds), `NOT_POSTED`, `FUND_INVALID`.
 */
export const changeARInvoiceFundSchema = z
    .object({ fundId: z.string().uuid().nullable() })
    .strict();
/**
 * The refusal codes of the AR invoice write RPCs that the web turns into a
 * translated message. The routes answer them as `{ error: <code>, code:
 * <code> }` (a 400); any other failure stays a message 400.
 *
 *   CHANNEL_MANAGED / FUND_MANAGED — the edit fences (a channel sale, a fund
 *     installment).
 *   LEGACY_POSTED — a draft still carrying a pre-20261101120000 posting;
 *     refused until the repair has run.
 *   HAS_PAYMENTS — money received (cancel, the issued edit, delete).
 *   ISSUED_LOCKED — date, supply date, customer, currency or AR control
 *     account of an issued invoice. The fund is NOT locked (Clay,
 *     2026-11-02): the full issued edit re-posts with the new fund outside a
 *     filed VAT period, and "Change fund" (`update_ar_invoice_fund`) moves it
 *     in place anywhere, a filed period included.
 *   CANCELLED — a cancelled invoice (edit, cancel again, change fund).
 *   ISSUED — delete of anything but a draft.
 *   USE_DELETE — cancel of a draft.
 *   USE_EDIT — change fund of a draft (its fund is edited in the form).
 *   NOT_DRAFT — issue of anything but a draft.
 *   ISSUE_DATE_FUTURE / ISSUE_DATE_BEFORE_LAST — the issue-date rule.
 *   CANCEL_DATE_FUTURE / CANCEL_DATE_BEFORE_INVOICE — the cancel-date rule.
 *   VAT_PERIOD_FILED — an issue / cancel date, or an issued invoice's own
 *     date on any edit, in a filed VAT period (the fund moves through
 *     "Change fund" instead).
 *   EXEMPTION_REASON_REQUIRED — issue of an invoice with a 0% / exempt line
 *     that states no exemption reason (VAT Directive art. 226(11), NL
 *     Wet OB art. 35a(1)(l), BE KB nr. 1 art. 5 §1 9°, FR CGI ann. II
 *     art. 242 nonies A I 12°); the body names the `line`. A draft may stay
 *     incomplete — the refusal is at Issue only, and channel sales and fund
 *     award invoices are exempt for now (20261101130000). A taxed line
 *     without its rate is NOT a code here: the posting helper's RV025 falls
 *     through to the API's `TAX_RATE_ID_REQUIRED` (`parseTaxRatePostingRefusal`).
 *   INVALID_STATUS / INVALID_TRANSITION — the status route.
 *   TAX_RATE_INVALID / FUND_INVALID / PROJECT_INVALID / CUSTOMER_INVALID —
 *     a line's tax rate, the fund, the project or the customer (business /
 *     person) is not one of the org's (create and edit).
 *   ACCOUNT_INVALID — a line's revenue account or the AR account is missing
 *     or not one of the org's (create, edit, issue).
 *   REASON_REQUIRED / REASON_TOO_LONG — cancel with a blank reason, or one
 *     over 500 characters (the route's zod pre-empts both; the RPC is the
 *     backstop).
 *   NOT_POSTED — cancel or change fund of an issued invoice that has no
 *     posting (legacy).
 *   NOT_AUTHENTICATED / FORBIDDEN / NOT_FOUND — the RPCs' own identity and
 *     existence checks (the route's middleware normally answers first).
 *
 * This list and the SQL suite's `v_ui` list (`ar_post_on_issue.sql` X5) are
 * the same set — the shared test pins it exactly, X5 pins the RPCs.
 */
export const AR_INVOICE_ERROR_CODES = [
    "CHANNEL_MANAGED",
    "FUND_MANAGED",
    "LEGACY_POSTED",
    "HAS_PAYMENTS",
    "ISSUED_LOCKED",
    "CANCELLED",
    "ISSUED",
    "USE_DELETE",
    "USE_EDIT",
    "NOT_DRAFT",
    "ISSUE_DATE_FUTURE",
    "ISSUE_DATE_BEFORE_LAST",
    "CANCEL_DATE_FUTURE",
    "CANCEL_DATE_BEFORE_INVOICE",
    "VAT_PERIOD_FILED",
    "EXEMPTION_REASON_REQUIRED",
    "INVALID_STATUS",
    "INVALID_TRANSITION",
    "TAX_RATE_INVALID",
    "FUND_INVALID",
    "PROJECT_INVALID",
    "CUSTOMER_INVALID",
    "ACCOUNT_INVALID",
    "REASON_REQUIRED",
    "REASON_TOO_LONG",
    "NOT_POSTED",
    "NOT_AUTHENTICATED",
    "FORBIDDEN",
    "NOT_FOUND",
];
/** POST .../ar-invoices/:invoiceId/apply-transaction — apply an existing ledger tx as an AR payment. */
export const applyTransactionToARInvoiceSchema = z
    .object({
    transaction_id: z.string().uuid(),
    ar_account_id: z.string().uuid(),
})
    .strict();
/**
 * POST .../ar-invoices/:invoiceId/undo-payment — undo a received payment.
 *
 * Wraps reverse_ar_payment: posts a reversing journal (the payment stays, a
 * mirror journal cancels it) dated period-aware, so amount_received / status
 * recompute downward with the audit trail intact. Bank-feed payments
 * (IMPORT_LINKED) and sales-channel receipts (CHANNEL_MANAGED) are refused.
 * The AR twin of undoAPPaymentSchema.
 */
export const undoARPaymentSchema = z
    .object({
    payment_transaction_id: z.string().uuid(),
})
    .strict();
//# sourceMappingURL=accountsReceivable.js.map